import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../styles/theme';
import ScrollLink from '../common/ScrollLink';
import MobileHeader from './mobile/MobileHeader';
import { useWebsiteAdminAuth } from '../../context/WebsiteAdminAuthContext';
import { WebsiteStorefrontPrimaryButton } from '../common/websiteSection';
import NeatScheduleMark from '../../../shared/components/brand/NeatScheduleMark';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(12px);
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  padding-top: calc(${theme.spacing.md} + env(safe-area-inset-top, 0px));
  border-bottom: 1px solid var(--login-form-border, ${theme.colors.secondary.dark});
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const NavLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(ScrollLink)`
  color: var(--login-form-muted, ${theme.colors.text.secondary});
  text-decoration: none;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: color 0.2s ease;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};

  &:hover {
    color: var(--login-primary, ${theme.colors.primary.main});
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-right: ${theme.spacing.md};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 50%;
  transform: ${props => props.isOpen ? 'translate(50%, 0)' : 'translate(50%, -8px)'};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  min-width: 220px;
  z-index: 1000;
  overflow: hidden;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  border: 1px solid ${theme.colors.secondary.dark};
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: none;
  border: none;
  text-align: left;
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: ${theme.colors.secondary.main};
    color: ${theme.colors.primary.main};
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${theme.colors.text.secondary};
    flex-shrink: 0;
  }

  &:hover svg {
    color: ${theme.colors.primary.main};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.secondary.dark};
  }

  &:last-child {
    color: #dc2626;
    &:hover {
      background-color: #fef2f2;
      color: #b91c1c;
    }
    svg {
      color: #dc2626;
    }
  }
`;

const DesktopHeader = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileHeaderWrapper = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const AdminBadge = WebsiteStorefrontPrimaryButton;

const AdminBadgeButton = WebsiteStorefrontPrimaryButton;

const AdminDropdownMenu = styled(DropdownMenu)`
  min-width: 220px;
`;

const AdminDropdownItem = styled(DropdownItem)`
  color: #dc2626;
  &:hover {
    background-color: #fef2f2;
    color: #b91c1c;
  }
  svg {
    color: #dc2626;
  }
`;

const HeaderButton = WebsiteStorefrontPrimaryButton;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const { adminUser, isSpecialAdmin, adminLogout } = useWebsiteAdminAuth();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isAdminDropdownOpen && !event.target.closest('.admin-dropdown')) {
        setIsAdminDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isAdminDropdownOpen]);

  const handleAuthAction = () => {
    navigate('/app/login');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setIsAdminDropdownOpen(false);

    const forceTop = () => {
      try { window.history.replaceState(null, '', '/'); } catch {}
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } catch {}
    };

    if (location.pathname !== '/') {
      navigate('/');
      requestAnimationFrame(forceTop);
      setTimeout(forceTop, 50);
    } else {
      forceTop();
      requestAnimationFrame(forceTop);
    }
  };

  const handleAdminLogout = async () => {
    try {
      await adminLogout();
      setIsAdminDropdownOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error logging out of admin:', error);
    }
  };

  return (
    <>
      <MobileHeaderWrapper>
        <MobileHeader />
      </MobileHeaderWrapper>
      <DesktopHeader>
        <HeaderContainer>
          <Nav>
            <LogoContainer>
              <Link to="/" onClick={handleLogoClick} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <NeatScheduleMark mark="header" />
              </Link>
            </LogoContainer>

            <NavLinks>
              <NavLink to="/why">Why Us</NavLink>
              <NavLink to="/#features">Features</NavLink>
              <NavLink to="/#pricing">Pricing</NavLink>
              <NavLink to="/#support">Support</NavLink>

              <ButtonGroup>
                {(adminUser && isSpecialAdmin) ? (
                  <>
                    <DropdownContainer className="admin-dropdown">
                      <AdminBadge
                        type="button"
                        onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm3 10.5a.75.75 0 000-1.5H9a.75.75 0 000 1.5h6z" clipRule="evenodd" />
                        </svg>
                        Admin
                      </AdminBadge>
                      <AdminDropdownMenu
                        isOpen={isAdminDropdownOpen}
                      >
                        <AdminDropdownItem onClick={handleAdminLogout}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Logout
                        </AdminDropdownItem>
                      </AdminDropdownMenu>
                    </DropdownContainer>
                    <AdminBadgeButton
                      type="button"
                      onClick={() => navigate('/app/login')}
                      title="Open the Neat Schedule app"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M3 6a3 3 0 013-3h2.25a3 3 0 013 3v2.25a3 3 0 01-3 3H6a3 3 0 01-3-3V6zm9.75 0a3 3 0 013-3H21a3 3 0 013 3v2.25a3 3 0 01-3 3h-5.25a3 3 0 01-3-3V6zM3 15.75a3 3 0 013-3h2.25a3 3 0 013 3V18a3 3 0 01-3 3H6a3 3 0 01-3-3v-2.25zm9.75 0a3 3 0 013-3H21a3 3 0 013 3V18a3 3 0 01-3 3h-5.25a3 3 0 01-3-3v-2.25z" clipRule="evenodd" />
                      </svg>
                      Open App
                    </AdminBadgeButton>
                  </>
                ) : (
                  <HeaderButton type="button" onClick={handleAuthAction}>
                    Log in
                  </HeaderButton>
                )}
              </ButtonGroup>
            </NavLinks>
          </Nav>
        </HeaderContainer>
      </DesktopHeader>
    </>
  );
};

export default Header;
